/**
 * Detection Rules v2 — base lib (type-agnostic surface only).
 *
 * Wave 2 per-type verticals (YARA/Sigma/Suricata/Elastic/Custom) will add
 * companion `lib/rules/{type}.ts` files that extend these types with per-type
 * meta shapes + typed content-save/lint/dry-run hooks. This file only covers
 * the cross-type surface backed by the admin-bff base handlers.
 *
 * Backing routes: cmd/admin-bff/rules_base.go. All paths are prefixed at
 * /api/v2 (nginx + Vite proxy strip the prefix on the backend side).
 *
 * Query key convention:
 *   ["rules", "list", filters, page]
 *   ["rules", "detail", id]
 *   ["rules", "detail", id, "versions"]
 *   ["rules", "detail", id, "version", n]
 *   ["rules", "reviews", filters]
 *   ["rules", "registries"]
 *   ["rules", "typeApprovers"]
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

// ---------- Types ----------

export type RuleType = "yara" | "sigma" | "suricata" | "elastic" | "custom";
export type RuleSeverity = "info" | "low" | "medium" | "high" | "critical";
export type RuleStatus = "draft" | "in_review" | "published" | "deprecated";
export type RuleTLP = "white" | "green" | "amber" | "red";
export type LintStatus = "unknown" | "ok" | "warn" | "error";
export type ReviewStatus = "pending" | "approved" | "rejected";

export interface Rule {
    id: string;
    type: RuleType;
    slug: string;
    name: string;
    severity: RuleSeverity;
    status: RuleStatus;
    currentVersion: number;
    publishedVersion?: number;
    tlp: RuleTLP;
    tags: string[];
    mitreTactics: string[];
    mitreTechniques: string[];
    description: string;
    imported: boolean;
    sourceUrl?: string;
    sourceRegistry?: string;
    license?: string;
    authorUserId?: string;
    createdAt: string;
    updatedAt: string;
    meta: Record<string, unknown>;
}

export interface LintMessage {
    line: number;
    col: number;
    severity: "error" | "warn" | "info";
    code: string;
    msg: string;
}

export interface RuleVersion {
    ruleId: string;
    version: number;
    content: string;
    parsed: Record<string, unknown>;
    lintStatus: LintStatus;
    lintMessages: LintMessage[];
    savedBy?: string;
    savedAt: string;
    note: string;
    snapshotName: string;
    snapshotSeverity: RuleSeverity;
    snapshotTags: string[];
}

export interface RuleReview {
    id: string;
    ruleId: string;
    ruleType: RuleType;
    version: number;
    requestedBy: string;
    requestedAt: string;
    approverUserId?: string;
    approvedAt?: string;
    rejectedAt?: string;
    note: string;
    status: ReviewStatus;
}

export interface RuleRegistry {
    slug: string;
    type: RuleType;
    name: string;
    url: string;
    license: string;
    extractor: string;
    pathFilter?: string;
    defaultTags: string[];
    syncCadenceHours: number;
    lastSyncAt?: string;
    enabled: boolean;
}

export interface TypeSeverityApprover {
    type: RuleType;
    severity: RuleSeverity;
    approversCount: number;
}

export interface RuleFilters {
    q?: string;
    type?: RuleType | "";
    status?: RuleStatus | "";
    tag?: string;
    imported?: boolean | null;
}

// ---------- Helpers ----------

function qstring(filters: RuleFilters, page: number, pageSize: number): string {
    const p = new URLSearchParams();
    if (filters.q) p.set("q", filters.q);
    if (filters.type) p.set("type", filters.type);
    if (filters.status) p.set("status", filters.status);
    if (filters.tag) p.set("tag", filters.tag);
    if (filters.imported !== undefined && filters.imported !== null) {
        p.set("imported", filters.imported ? "true" : "false");
    }
    p.set("limit", String(pageSize));
    p.set("offset", String(page * pageSize));
    return p.toString();
}

// ---------- Queries ----------

export function useBaseRules(filters: RuleFilters, page = 0, pageSize = 50) {
    return useQuery({
        queryKey: ["rules", "list", filters, page, pageSize] as const,
        queryFn: () =>
            api.get<{ rules: Rule[]; total: number }>(`/api/v2/rules?${qstring(filters, page, pageSize)}`),
        staleTime: 5_000,
    });
}

export function useRule(id: string | null | undefined) {
    return useQuery({
        queryKey: ["rules", "detail", id] as const,
        queryFn: () => api.get<{ rule: Rule }>(`/api/v2/rules/${id}`).then((d) => d.rule),
        enabled: !!id,
        staleTime: 5_000,
    });
}

export function useRuleVersions(id: string | null | undefined) {
    return useQuery({
        queryKey: ["rules", "detail", id, "versions"] as const,
        queryFn: () =>
            api
                .get<{ versions: RuleVersion[] }>(`/api/v2/rules/${id}/versions`)
                .then((d) => d.versions ?? []),
        enabled: !!id,
        staleTime: 5_000,
    });
}

export function useRuleVersion(id: string | null | undefined, version: number | null | undefined) {
    return useQuery({
        queryKey: ["rules", "detail", id, "version", version] as const,
        queryFn: () =>
            api
                .get<{ version: RuleVersion }>(`/api/v2/rules/${id}/versions/${version}`)
                .then((d) => d.version),
        enabled: !!id && !!version,
        staleTime: 30_000,
    });
}

export function useRegistries(type?: RuleType) {
    const url = type ? `/api/v2/rule-registries?type=${type}` : `/api/v2/rule-registries`;
    return useQuery({
        queryKey: ["rules", "registries", type ?? "all"] as const,
        queryFn: () =>
            api.get<{ registries: RuleRegistry[] }>(url).then((d) => d.registries ?? []),
        staleTime: 5 * 60_000,
    });
}

export function useTypeSeverityApprovers() {
    return useQuery({
        queryKey: ["rules", "typeApprovers"] as const,
        queryFn: () =>
            api
                .get<{ typeApprovers: TypeSeverityApprover[] }>(`/api/v2/rule-type-approvers`)
                .then((d) => d.typeApprovers ?? []),
        staleTime: 60_000,
    });
}

export interface ReviewQueueFilters {
    type?: RuleType | "";
    status?: "pending" | "";
    ruleId?: string;
}

export function useReviewQueue(filters: ReviewQueueFilters = { status: "pending" }) {
    const p = new URLSearchParams();
    if (filters.type) p.set("type", filters.type);
    if (filters.status) p.set("status", filters.status);
    if (filters.ruleId) p.set("ruleId", filters.ruleId);
    return useQuery({
        queryKey: ["rules", "reviews", filters] as const,
        queryFn: () =>
            api.get<{ reviews: RuleReview[] }>(`/api/v2/reviews?${p.toString()}`).then((d) => d.reviews ?? []),
        staleTime: 5_000,
    });
}

// ---------- Mutations ----------

export interface PatchRuleInput {
    name?: string;
    severity?: RuleSeverity;
    tlp?: RuleTLP;
    tags?: string[];
    mitreTactics?: string[];
    mitreTechniques?: string[];
    description?: string;
    status?: RuleStatus;
}

export function usePatchRule(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: PatchRuleInput) =>
            api.patch<{ rule: Rule }>(`/api/v2/rules/${id}`, patch).then((d) => d.rule),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "list"] });
        },
    });
}

export function useDeleteRule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => api.delete<void>(`/api/v2/rules/${id}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "list"] });
        },
    });
}

export interface SubmitReviewInput {
    version?: number;
    note: string;
}

export function useSubmitForReview(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: SubmitReviewInput) =>
            api.post<{ review: RuleReview }>(`/api/v2/rules/${id}/reviews`, input).then((d) => d.review),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "reviews"] });
        },
    });
}

export function useApprove(reviewId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (note?: string) =>
            api
                .post<{ review: RuleReview }>(`/api/v2/reviews/${reviewId}/approve`, note ? { note } : {})
                .then((d) => d.review),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "reviews"] });
            void qc.invalidateQueries({ queryKey: ["rules", "detail"] });
            void qc.invalidateQueries({ queryKey: ["rules", "list"] });
        },
    });
}

export function useReject(reviewId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (note?: string) =>
            api
                .post<{ review: RuleReview }>(`/api/v2/reviews/${reviewId}/reject`, note ? { note } : {})
                .then((d) => d.review),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "reviews"] });
            void qc.invalidateQueries({ queryKey: ["rules", "detail"] });
        },
    });
}

export function useSetTypeSeverityApprover() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { type: RuleType; severity: RuleSeverity; approversCount: number }) =>
            api.put<{ typeApprover: TypeSeverityApprover }>(
                `/api/v2/rule-type-approvers/${input.type}/${input.severity}`,
                { approversCount: input.approversCount },
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "typeApprovers"] });
        },
    });
}

// ---------- Constants ----------

export const RULE_TYPES: ReadonlyArray<{ value: RuleType; label: string; chipClass: string }> = [
    { value: "yara", label: "YARA", chipClass: "chip--warn" },
    { value: "sigma", label: "Sigma", chipClass: "chip--accent" },
    { value: "suricata", label: "Suricata", chipClass: "chip--danger" },
    { value: "elastic", label: "Elastic", chipClass: "chip--info" },
    { value: "custom", label: "Custom", chipClass: "chip--subtle" },
];

export const RULE_SEVERITIES: ReadonlyArray<{ value: RuleSeverity; pillClass: string }> = [
    { value: "info", pillClass: "status-pill--queued" },
    { value: "low", pillClass: "status-pill--paused" },
    { value: "medium", pillClass: "status-pill--live" },
    { value: "high", pillClass: "status-pill--failed" },
    { value: "critical", pillClass: "status-pill--failed" },
];

export const RULE_STATUSES: ReadonlyArray<{ value: RuleStatus; label: string; pillClass: string }> = [
    { value: "draft", label: "Draft", pillClass: "status-pill--queued" },
    { value: "in_review", label: "In review", pillClass: "status-pill--paused" },
    { value: "published", label: "Published", pillClass: "status-pill--live" },
    { value: "deprecated", label: "Deprecated", pillClass: "status-pill--failed" },
];

export function typeChipClass(t: RuleType): string {
    return RULE_TYPES.find((l) => l.value === t)?.chipClass ?? "chip--subtle";
}

export function typeLabel(t: RuleType): string {
    return RULE_TYPES.find((l) => l.value === t)?.label ?? t;
}

export function severityPillClass(sev: RuleSeverity): string {
    return RULE_SEVERITIES.find((s) => s.value === sev)?.pillClass ?? "status-pill--queued";
}

export function statusPillClass(status: RuleStatus): string {
    return RULE_STATUSES.find((s) => s.value === status)?.pillClass ?? "status-pill--queued";
}
